import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import PortalSidebar from '../components/PortalSidebar';
import PaymentUploadModal from '../components/PaymentUploadModal';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const STAGES = [
  { key: 'LEAD', label: 'Lead Generated', desc: 'Initial inquiry received and processed.', icon: 'check_circle' },
  { key: 'AGENT_ASSIGNED', label: 'Agent Assigned', desc: 'A consultant has been assigned to your case.', icon: 'person_check' },
  { key: 'DOCUMENT_COLLECTION', label: 'Document Collection', desc: 'We are collecting your required documents.', icon: 'description' },
  { key: 'INITIAL_FEE_PAID', label: 'Fee Paid', desc: 'Processing and consultancy fees confirmed.', icon: 'payments' },
  { key: 'SUBMITTED', label: 'Submitted', desc: 'Final review and formal submission to the institution/employer.', icon: 'send' },
  { key: 'OFFER_RECEIVED', label: 'Offer Received', desc: 'Official communication regarding your application status.', icon: 'school' },
  { key: 'DEPOSIT_PAID', label: 'Deposit Paid', desc: 'Tuition deposit or job-matching fee confirmed.', icon: 'account_balance_wallet' },
  { key: 'OFFICIAL_ACCEPTANCE', label: 'Official Acceptance', desc: 'Your official offer/acceptance has been confirmed.', icon: 'verified' },
  { key: 'VISA_APPLICATION', label: 'Visa Application', desc: 'Visa documents and fee submitted to the embassy. Final stage of our managed process.', icon: 'badge' },
  { key: 'CLOSED', label: 'Closed', desc: 'This application has been closed.', icon: 'lock' },
];

export default function ApplicationTracker() {
  const { id } = useParams();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  const fetchApplication = () => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/portal/applications/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((json) => setApplication(json.success ? json.data : null))
      .finally(() => setLoading(false));
  };

  useEffect(fetchApplication, [id]);

  if (loading) return <p className="text-center py-40 text-[#64748b]">Loading application...</p>;
  if (!application) return <p className="text-center py-40 text-[#64748b]">Application not found.</p>;

  const currentIndex = STAGES.findIndex((s) => s.key === application.currentStage);
  const logByStage = {};
  (application.statusLogs || []).forEach((log) => { logByStage[log.toStage] = log; });

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <PortalSidebar />
      <main className="md:ml-64 pt-24 min-h-screen">
        <div className="max-w-[1280px] mx-auto px-6 pb-12">
          {/* Summary Bar */}
          <div className="bg-white rounded-xl shadow-[0px_10px_30px_rgba(30,60,114,0.05)] p-6 mb-8 flex flex-wrap items-center justify-between gap-6 border border-[#e2e8f0]">
            <div className="flex items-center gap-6">
              <div className="flex flex-col">
                <span className="text-xs text-[#64748b] uppercase tracking-wider">Destination</span>
                <span className="text-xl font-bold text-[#00255a]">{application.opportunity?.country || 'N/A'}</span>
              </div>
              <div className="h-10 w-px bg-[#e2e8f0] hidden sm:block" />
              <div className="flex flex-col">
                <span className="text-xs text-[#64748b] uppercase tracking-wider">Program</span>
                <span className="text-xl font-bold text-[#00255a]">{application.opportunity?.title || 'General Application'}</span>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-[#f2f4f6] px-4 py-2 rounded-lg border border-[#e2e8f0]">
              <span className="material-symbols-outlined text-[#365da3]">fingerprint</span>
              <div className="flex flex-col">
                <span className="text-[10px] font-bold text-[#64748b] uppercase">Application ID</span>
                <span className="text-sm font-semibold text-[#365da3]">{application.id.slice(0, 8).toUpperCase()}</span>
              </div>
            </div>
            {application.approvedPdfUrl && (
              <a
                href={application.approvedPdfUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-white px-5 py-2.5 rounded-lg font-bold text-sm shadow-md hover:scale-[1.02] transition-transform"
                style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
              >
                <span className="material-symbols-outlined text-[18px]">download</span>
                Download Confirmation
              </a>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Pipeline */}
            <div className="lg:col-span-8">
              <div className="bg-white rounded-xl shadow-[0px_10px_30px_rgba(30,60,114,0.05)] border border-[#e2e8f0] p-8">
                <h3 className="text-xl font-bold text-[#00255a] mb-8">Application Pipeline</h3>
                <div className="relative space-y-12">
                  {STAGES.map((stage, i) => {
                    const isCompleted = i < currentIndex;
                    const isCurrent = i === currentIndex;
                    const isUpcoming = i > currentIndex;
                    const log = logByStage[stage.key];

                    return (
                      <div key={stage.key} className={`relative flex items-start group ${isUpcoming ? 'opacity-50' : ''}`}>
                        {i < STAGES.length - 1 && (
                          <div
                            className="absolute w-0.5 left-5 top-10 bottom-[-3rem]"
                            style={{ background: isCompleted ? 'linear-gradient(to bottom, #00255a, #ff7b7b)' : '#e2e8f0' }}
                          />
                        )}
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center z-10 ${
                            isCompleted ? 'bg-[#00255a]' : isCurrent ? 'text-white shadow-lg' : 'bg-[#e6e8ea] border-2 border-[#c4c6d1]'
                          }`}
                          style={isCurrent ? { background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' } : {}}
                        >
                          <span className={`material-symbols-outlined ${isCompleted ? 'text-[#ff7b7b]' : isCurrent ? 'text-white animate-pulse' : 'text-[#747781]'}`}>
                            {isCompleted ? 'check_circle' : stage.icon}
                          </span>
                        </div>
                        {isCurrent ? (
                          <div className="ml-6 flex-1 bg-[#f2f4f6] p-6 rounded-xl border-2 border-[#365da3]/20">
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="text-lg font-bold text-[#00255a]">{stage.label}</h4>
                              <span className="bg-[#8fb3ff] text-[#174388] px-2 py-0.5 rounded text-[10px] font-bold uppercase">In Progress</span>
                            </div>
                            <p className="text-[#44474f] mb-4">{stage.desc}</p>
                            <div className="flex gap-3 flex-wrap">
                              <Link to={`/portal/applications/${id}/documents`} className="bg-[#00255a] text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2">
                                <span className="material-symbols-outlined text-sm">upload_file</span>
                                Upload Files
                              </Link>
                              <Link to={`/portal/applications/${id}/documents`} className="border border-[#747781] text-[#44474f] px-4 py-2 rounded-lg text-sm font-bold">
                                View Checklist
                              </Link>
                            </div>
                          </div>
                        ) : (
                          <div className="ml-6 flex-1">
                            <h4 className={`text-lg font-bold ${isCompleted ? 'text-[#00255a]' : 'text-[#64748b]'}`}>{stage.label}</h4>
                            <p className="text-[#64748b]">
                              {isCompleted && log
                                ? `${log.note || stage.desc} — ${new Date(log.createdAt).toLocaleDateString()}`
                                : stage.desc}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-4 space-y-8">
              {application.assignedAgent ? (
                <div className="bg-white rounded-xl border border-[#e2e8f0] shadow-[0px_10px_30px_rgba(30,60,114,0.05)] overflow-hidden">
                  <div className="h-24" style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }} />
                  <div className="px-6 pb-6 -mt-12">
                    <div className="relative inline-block mb-4">
                      <div className="w-24 h-24 rounded-2xl bg-[#8fb3ff] border-4 border-white shadow-md flex items-center justify-center text-white text-3xl font-bold">
                        {application.assignedAgent.fullName?.charAt(0)}
                      </div>
                      <div className="absolute bottom-1 right-1 w-5 h-5 bg-green-500 border-2 border-white rounded-full" />
                    </div>
                    <h3 className="text-lg font-bold text-[#00255a]">{application.assignedAgent.fullName}</h3>
                    <p className="text-sm text-[#365da3] mb-4">Global Admissions Agent</p>
                    <div className="space-y-3">
                      <a
                        href={`https://wa.me/${application.assignedAgent.whatsapp?.replace(/\D/g, '')}`}
                        className="w-full flex items-center justify-center gap-3 bg-[#25D366] text-white py-3 rounded-lg font-bold hover:opacity-90 transition-opacity"
                      >
                        <span className="material-symbols-outlined text-sm">chat</span>
                        <span>WhatsApp {application.assignedAgent.fullName?.split(' ')[0]}</span>
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-[#e2e8f0] p-6 text-center text-[#64748b]">
                  Awaiting agent assignment.
                </div>
              )}

              <div className="bg-white rounded-xl border border-[#e2e8f0] shadow-[0px_10px_30px_rgba(30,60,114,0.05)] p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-[#00255a]">Payments</h3>
                  <button
                    onClick={() => setPaymentModalOpen(true)}
                    className="text-xs font-bold text-white px-3 py-2 rounded-lg flex items-center gap-1"
                    style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
                  >
                    <span className="material-symbols-outlined text-[16px]">upload</span>
                    Upload
                  </button>
                </div>
                {(application.payments || []).length === 0 ? (
                  <p className="text-sm text-[#64748b]">No payments submitted yet.</p>
                ) : (
                  <div className="space-y-3">
                    {application.payments.map((p) => (
                      <div key={p.id} className="flex items-center justify-between p-3 bg-[#f2f4f6] rounded-lg">
                        <div>
                          <p className="text-sm font-semibold text-[#0f172a]">{p.label}</p>
                          <p className="text-xs text-[#64748b]">{p.currency} {Number(p.amount).toLocaleString()}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${
                          p.status === 'CONFIRMED' ? 'bg-emerald-100 text-emerald-700' :
                          p.status === 'REJECTED' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {p.status.replace('_', ' ')}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-[#1e3c72] text-white rounded-xl p-6 relative overflow-hidden">
                <div className="relative z-10">
                  <h4 className="text-lg font-bold mb-2">Pro Tip</h4>
                  <p className="opacity-90 text-sm">Certified documents speed up the process. Ensure all scans are in high-resolution PDF format.</p>
                </div>
                <span className="material-symbols-outlined absolute -right-4 -bottom-4 text-9xl opacity-10">tips_and_updates</span>
              </div>
            </div>
          </div>
        </div>
      </main>
      {paymentModalOpen && (
        <PaymentUploadModal
          applicationId={id}
          onClose={() => setPaymentModalOpen(false)}
          onSuccess={() => { setPaymentModalOpen(false); fetchApplication(); }}
        />
      )}
    </div>
  );
}